import { Language } from './i18n'

// ─── Static dictionary: Spanish → other languages ───────────────────────────
// Keys are LOWERCASE Spanish strings. Values are { en, de, pt, zh }.
type LangKey = Exclude<Language, 'es'>
type TranslationEntry = Partial<Record<LangKey, string>>

const dictionary: Record<string, TranslationEntry> = {
  // ═══════════════════════════════════════════════════════════════════════════
  // CATEGORY NAMES (from PARENT_CATEGORIES subcategories in RestaurantPage)
  // ═══════════════════════════════════════════════════════════════════════════
  'despertares del valle': {
    en: 'Valley Breakfasts',
    de: 'Frühstück aus dem Tal',
    pt: 'Cafés da Manhã do Vale',
    zh: '山谷早餐',
  },
  'sabores del mediodía': {
    en: 'Midday Flavors',
    de: 'Mittagsaromen',
    pt: 'Sabores do Meio-dia',
    zh: '午间风味',
  },
  ensaladas: { en: 'Salads', de: 'Salate', pt: 'Saladas', zh: '沙拉' },
  'platos con memoria': {
    en: 'Signature Dishes',
    de: 'Gerichte mit Geschichte',
    pt: 'Pratos com Memória',
    zh: '招牌菜',
  },
  'de la plancha al corazón': {
    en: 'From the Grill',
    de: 'Vom Grill',
    pt: 'Da Grelha ao Coração',
    zh: '铁板烧烤',
  },
  'bebidas calientes a base de café': {
    en: 'Hot Coffee Drinks',
    de: 'Heiße Kaffeegetränke',
    pt: 'Bebidas Quentes de Café',
    zh: '热咖啡饮品',
  },
  'bebidas calientes': {
    en: 'Hot Drinks',
    de: 'Heiße Getränke',
    pt: 'Bebidas Quentes',
    zh: '热饮',
  },
  'bebidas frías a base de café': {
    en: 'Iced Coffee Drinks',
    de: 'Kalte Kaffeegetränke',
    pt: 'Bebidas Geladas de Café',
    zh: '冰咖啡饮品',
  },
  'bebidas frías': {
    en: 'Cold Drinks',
    de: 'Kalte Getränke',
    pt: 'Bebidas Frias',
    zh: '冷饮',
  },
  'shakes y smoothies': {
    en: 'Shakes & Smoothies',
    de: 'Shakes & Smoothies',
    pt: 'Shakes e Smoothies',
    zh: '奶昔和冰沙',
  },
  funcionales: {
    en: 'Functional Drinks',
    de: 'Funktionale Getränke',
    pt: 'Bebidas Funcionais',
    zh: '功能饮品',
  },
  'bubble tea': {
    en: 'Bubble Tea',
    de: 'Bubble Tea',
    pt: 'Bubble Tea',
    zh: '珍珠奶茶',
  },
  cervezas: { en: 'Beers', de: 'Biere', pt: 'Cervejas', zh: '啤酒' },
  vinos: { en: 'Wines', de: 'Weine', pt: 'Vinhos', zh: '葡萄酒' },
  cócteles: { en: 'Cocktails', de: 'Cocktails', pt: 'Coquetéis', zh: '鸡尾酒' },
  mócteles: {
    en: 'Mocktails',
    de: 'Mocktails',
    pt: 'Mocktails',
    zh: '无酒精鸡尾酒',
  },
  alcohol: { en: 'Alcohol', de: 'Alkohol', pt: 'Álcool', zh: '酒精饮品' },
  postres: { en: 'Desserts', de: 'Desserts', pt: 'Sobremesas', zh: '甜点' },
  'para los pequeños': {
    en: 'For the Little Ones',
    de: 'Für die Kleinen',
    pt: 'Para os Pequenos',
    zh: '儿童餐',
  },
  pizzas: { en: 'Pizzas', de: 'Pizzen', pt: 'Pizzas', zh: '披萨' },
  bebidas: { en: 'Drinks', de: 'Getränke', pt: 'Bebidas', zh: '饮品' },
  desayunos: {
    en: 'Breakfasts',
    de: 'Frühstück',
    pt: 'Cafés da Manhã',
    zh: '早餐',
  },
  almuerzos: { en: 'Lunches', de: 'Mittagessen', pt: 'Almoços', zh: '午餐' },
  cenas: { en: 'Dinners', de: 'Abendessen', pt: 'Jantares', zh: '晚餐' },
  entradas: { en: 'Starters', de: 'Vorspeisen', pt: 'Entradas', zh: '开胃菜' },
  sopas: { en: 'Soups', de: 'Suppen', pt: 'Sopas', zh: '汤类' },
  acompañamientos: {
    en: 'Sides',
    de: 'Beilagen',
    pt: 'Acompanhamentos',
    zh: '配菜',
  },
  especialidades: {
    en: 'Specialties',
    de: 'Spezialitäten',
    pt: 'Especialidades',
    zh: '特色菜',
  },
  'platos fuertes': {
    en: 'Main Courses',
    de: 'Hauptgerichte',
    pt: 'Pratos Principais',
    zh: '主菜',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FRUITS / FRUTAS
  // ═══════════════════════════════════════════════════════════════════════════
  manzana: { en: 'apple', de: 'Apfel', pt: 'maçã', zh: '苹果' },
  manzanas: { en: 'apples', de: 'Äpfel', pt: 'maçãs', zh: '苹果' },
  fresa: { en: 'strawberry', de: 'Erdbeere', pt: 'morango', zh: '草莓' },
  fresas: { en: 'strawberries', de: 'Erdbeeren', pt: 'morangos', zh: '草莓' },
  banano: { en: 'banana', de: 'Banane', pt: 'banana', zh: '香蕉' },
  banana: { en: 'banana', de: 'Banane', pt: 'banana', zh: '香蕉' },
  piña: { en: 'pineapple', de: 'Ananas', pt: 'abacaxi', zh: '菠萝' },
  mango: { en: 'mango', de: 'Mango', pt: 'manga', zh: '芒果' },
  papaya: { en: 'papaya', de: 'Papaya', pt: 'mamão', zh: '木瓜' },
  sandía: { en: 'watermelon', de: 'Wassermelone', pt: 'melancia', zh: '西瓜' },
  melón: { en: 'melon', de: 'Melone', pt: 'melão', zh: '甜瓜' },
  naranja: { en: 'orange', de: 'Orange', pt: 'laranja', zh: '橙子' },
  limón: { en: 'lemon', de: 'Zitrone', pt: 'limão', zh: '柠檬' },
  lima: { en: 'lime', de: 'Limette', pt: 'lima', zh: '青柠' },
  uva: { en: 'grape', de: 'Traube', pt: 'uva', zh: '葡萄' },
  uvas: { en: 'grapes', de: 'Trauben', pt: 'uvas', zh: '葡萄' },
  mora: { en: 'blackberry', de: 'Brombeere', pt: 'amora', zh: '黑莓' },
  moras: { en: 'blackberries', de: 'Brombeeren', pt: 'amoras', zh: '黑莓' },
  arándano: { en: 'blueberry', de: 'Blaubeere', pt: 'mirtilo', zh: '蓝莓' },
  arándanos: {
    en: 'blueberries',
    de: 'Blaubeeren',
    pt: 'mirtilos',
    zh: '蓝莓',
  },
  coco: { en: 'coconut', de: 'Kokosnuss', pt: 'coco', zh: '椰子' },
  maracuyá: {
    en: 'passion fruit',
    de: 'Maracuja',
    pt: 'maracujá',
    zh: '百香果',
  },
  guanábana: {
    en: 'soursop',
    de: 'Stachelannone',
    pt: 'graviola',
    zh: '刺果番荔枝',
  },
  cas: { en: 'cas fruit', de: 'Cas-Frucht', pt: 'cas', zh: '酸番石榴' },
  guayaba: { en: 'guava', de: 'Guave', pt: 'goiaba', zh: '番石榴' },
  uchuva: { en: 'golden berry', de: 'Physalis', pt: 'physalis', zh: '灯笼果' },
  aguacate: { en: 'avocado', de: 'Avocado', pt: 'abacate', zh: '牛油果' },
  tomate: { en: 'tomato', de: 'Tomate', pt: 'tomate', zh: '番茄' },
  tomates: { en: 'tomatoes', de: 'Tomaten', pt: 'tomates', zh: '番茄' },

  // ═══════════════════════════════════════════════════════════════════════════
  // VEGETABLES / VERDURAS
  // ═══════════════════════════════════════════════════════════════════════════
  lechuga: { en: 'lettuce', de: 'Salat', pt: 'alface', zh: '生菜' },
  espinaca: { en: 'spinach', de: 'Spinat', pt: 'espinafre', zh: '菠菜' },
  espinacas: { en: 'spinach', de: 'Spinat', pt: 'espinafre', zh: '菠菜' },
  cebolla: { en: 'onion', de: 'Zwiebel', pt: 'cebola', zh: '洋葱' },
  'cebolla morada': {
    en: 'red onion',
    de: 'rote Zwiebel',
    pt: 'cebola roxa',
    zh: '紫洋葱',
  },
  ajo: { en: 'garlic', de: 'Knoblauch', pt: 'alho', zh: '大蒜' },
  pimiento: { en: 'bell pepper', de: 'Paprika', pt: 'pimentão', zh: '甜椒' },
  pimientos: { en: 'bell peppers', de: 'Paprika', pt: 'pimentões', zh: '甜椒' },
  chile: { en: 'chili', de: 'Chili', pt: 'pimenta', zh: '辣椒' },
  jalapeño: {
    en: 'jalapeño',
    de: 'Jalapeño',
    pt: 'jalapeño',
    zh: '墨西哥辣椒',
  },
  zanahoria: { en: 'carrot', de: 'Karotte', pt: 'cenoura', zh: '胡萝卜' },
  pepino: { en: 'cucumber', de: 'Gurke', pt: 'pepino', zh: '黄瓜' },
  champiñones: { en: 'mushrooms', de: 'Pilze', pt: 'cogumelos', zh: '蘑菇' },
  champiñón: { en: 'mushroom', de: 'Pilz', pt: 'cogumelo', zh: '蘑菇' },
  hongos: { en: 'mushrooms', de: 'Pilze', pt: 'cogumelos', zh: '蘑菇' },
  maíz: { en: 'corn', de: 'Mais', pt: 'milho', zh: '玉米' },
  elote: { en: 'corn', de: 'Mais', pt: 'milho', zh: '玉米' },
  brócoli: { en: 'broccoli', de: 'Brokkoli', pt: 'brócolis', zh: '西兰花' },
  palmito: { en: 'heart of palm', de: 'Palmherz', pt: 'palmito', zh: '棕榈心' },
  verduras: { en: 'vegetables', de: 'Gemüse', pt: 'verduras', zh: '蔬菜' },
  'verduras salteadas': {
    en: 'sautéed vegetables',
    de: 'gebratenes Gemüse',
    pt: 'verduras salteadas',
    zh: '炒蔬菜',
  },
  vegetales: { en: 'vegetables', de: 'Gemüse', pt: 'vegetais', zh: '蔬菜' },
  rúcula: { en: 'arugula', de: 'Rucola', pt: 'rúcula', zh: '芝麻菜' },
  albahaca: { en: 'basil', de: 'Basilikum', pt: 'manjericão', zh: '罗勒' },
  cilantro: { en: 'cilantro', de: 'Koriander', pt: 'coentro', zh: '香菜' },
  perejil: { en: 'parsley', de: 'Petersilie', pt: 'salsinha', zh: '欧芹' },
  hierbas: { en: 'herbs', de: 'Kräuter', pt: 'ervas', zh: '香草' },
  aceitunas: { en: 'olives', de: 'Oliven', pt: 'azeitonas', zh: '橄榄' },
  aceituna: { en: 'olive', de: 'Olive', pt: 'azeitona', zh: '橄榄' },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROTEINS / PROTEÍNAS
  // ═══════════════════════════════════════════════════════════════════════════
  pollo: { en: 'chicken', de: 'Hähnchen', pt: 'frango', zh: '鸡肉' },
  carne: { en: 'meat', de: 'Fleisch', pt: 'carne', zh: '肉' },
  'carne de res': {
    en: 'beef',
    de: 'Rindfleisch',
    pt: 'carne bovina',
    zh: '牛肉',
  },
  res: { en: 'beef', de: 'Rind', pt: 'boi', zh: '牛肉' },
  cerdo: { en: 'pork', de: 'Schweinefleisch', pt: 'porco', zh: '猪肉' },
  tocino: { en: 'bacon', de: 'Speck', pt: 'bacon', zh: '培根' },
  bacon: { en: 'bacon', de: 'Speck', pt: 'bacon', zh: '培根' },
  jamón: { en: 'ham', de: 'Schinken', pt: 'presunto', zh: '火腿' },
  salami: { en: 'salami', de: 'Salami', pt: 'salame', zh: '萨拉米' },
  pepperoni: {
    en: 'pepperoni',
    de: 'Pepperoni',
    pt: 'pepperoni',
    zh: '意大利辣香肠',
  },
  chorizo: { en: 'chorizo', de: 'Chorizo', pt: 'chouriço', zh: '西班牙香肠' },
  salchicha: { en: 'sausage', de: 'Wurst', pt: 'salsicha', zh: '香肠' },
  pescado: { en: 'fish', de: 'Fisch', pt: 'peixe', zh: '鱼' },
  atún: { en: 'tuna', de: 'Thunfisch', pt: 'atum', zh: '金枪鱼' },
  salmón: { en: 'salmon', de: 'Lachs', pt: 'salmão', zh: '三文鱼' },
  camarones: { en: 'shrimp', de: 'Garnelen', pt: 'camarões', zh: '虾' },
  mariscos: {
    en: 'seafood',
    de: 'Meeresfrüchte',
    pt: 'frutos do mar',
    zh: '海鲜',
  },
  huevo: { en: 'egg', de: 'Ei', pt: 'ovo', zh: '鸡蛋' },
  huevos: { en: 'eggs', de: 'Eier', pt: 'ovos', zh: '鸡蛋' },
  'huevos revueltos': {
    en: 'scrambled eggs',
    de: 'Rührei',
    pt: 'ovos mexidos',
    zh: '炒蛋',
  },
  'huevos fritos': {
    en: 'fried eggs',
    de: 'Spiegeleier',
    pt: 'ovos fritos',
    zh: '煎蛋',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DAIRY / LÁCTEOS
  // ═══════════════════════════════════════════════════════════════════════════
  queso: { en: 'cheese', de: 'Käse', pt: 'queijo', zh: '奶酪' },
  'queso mozzarella': {
    en: 'mozzarella cheese',
    de: 'Mozzarella',
    pt: 'queijo mussarela',
    zh: '马苏里拉奶酪',
  },
  mozzarella: {
    en: 'mozzarella',
    de: 'Mozzarella',
    pt: 'mussarela',
    zh: '马苏里拉',
  },
  'queso cheddar': {
    en: 'cheddar cheese',
    de: 'Cheddar-Käse',
    pt: 'queijo cheddar',
    zh: '切达奶酪',
  },
  'queso parmesano': {
    en: 'parmesan cheese',
    de: 'Parmesan',
    pt: 'queijo parmesão',
    zh: '帕尔马干酪',
  },
  parmesano: {
    en: 'parmesan',
    de: 'Parmesan',
    pt: 'parmesão',
    zh: '帕尔马干酪',
  },
  'queso crema': {
    en: 'cream cheese',
    de: 'Frischkäse',
    pt: 'cream cheese',
    zh: '奶油奶酪',
  },
  crema: { en: 'cream', de: 'Sahne', pt: 'creme', zh: '奶油' },
  'crema agria': {
    en: 'sour cream',
    de: 'Sauerrahm',
    pt: 'creme azedo',
    zh: '酸奶油',
  },
  natilla: { en: 'sour cream', de: 'Sauerrahm', pt: 'nata', zh: '酸奶油' },
  leche: { en: 'milk', de: 'Milch', pt: 'leite', zh: '牛奶' },
  'leche de almendras': {
    en: 'almond milk',
    de: 'Mandelmilch',
    pt: 'leite de amêndoas',
    zh: '杏仁奶',
  },
  'leche de coco': {
    en: 'coconut milk',
    de: 'Kokosmilch',
    pt: 'leite de coco',
    zh: '椰奶',
  },
  yogurt: { en: 'yogurt', de: 'Joghurt', pt: 'iogurte', zh: '酸奶' },
  'yogurt natural': {
    en: 'natural yogurt',
    de: 'Naturjoghurt',
    pt: 'iogurte natural',
    zh: '原味酸奶',
  },
  'yogurt griego': {
    en: 'Greek yogurt',
    de: 'griechischer Joghurt',
    pt: 'iogurte grego',
    zh: '希腊酸奶',
  },
  mantequilla: { en: 'butter', de: 'Butter', pt: 'manteiga', zh: '黄油' },

  // ═══════════════════════════════════════════════════════════════════════════
  // GRAINS, CEREALS, BREAD / GRANOS, CEREALES, PAN
  // ═══════════════════════════════════════════════════════════════════════════
  arroz: { en: 'rice', de: 'Reis', pt: 'arroz', zh: '米饭' },
  'arroz blanco': {
    en: 'white rice',
    de: 'weißer Reis',
    pt: 'arroz branco',
    zh: '白米饭',
  },
  frijoles: { en: 'beans', de: 'Bohnen', pt: 'feijão', zh: '豆子' },
  'frijoles negros': {
    en: 'black beans',
    de: 'schwarze Bohnen',
    pt: 'feijão preto',
    zh: '黑豆',
  },
  'gallo pinto': {
    en: 'gallo pinto (rice & beans)',
    de: 'Gallo Pinto (Reis & Bohnen)',
    pt: 'gallo pinto (arroz e feijão)',
    zh: '加洛平托（米饭和豆子）',
  },
  pan: { en: 'bread', de: 'Brot', pt: 'pão', zh: '面包' },
  'pan artesanal': {
    en: 'artisan bread',
    de: 'Handwerksbrot',
    pt: 'pão artesanal',
    zh: '手工面包',
  },
  tortilla: { en: 'tortilla', de: 'Tortilla', pt: 'tortilha', zh: '玉米饼' },
  tortillas: {
    en: 'tortillas',
    de: 'Tortillas',
    pt: 'tortilhas',
    zh: '玉米饼',
  },
  tostada: { en: 'toast', de: 'Toast', pt: 'torrada', zh: '吐司' },
  tostadas: { en: 'toasts', de: 'Toasts', pt: 'torradas', zh: '吐司' },
  granola: { en: 'granola', de: 'Granola', pt: 'granola', zh: '格兰诺拉麦片' },
  'granola artesanal': {
    en: 'artisan granola',
    de: 'handgemachtes Granola',
    pt: 'granola artesanal',
    zh: '手工格兰诺拉',
  },
  avena: { en: 'oats', de: 'Hafer', pt: 'aveia', zh: '燕麦' },
  pasta: { en: 'pasta', de: 'Pasta', pt: 'massa', zh: '意面' },
  fideos: { en: 'noodles', de: 'Nudeln', pt: 'macarrão', zh: '面条' },

  // ═══════════════════════════════════════════════════════════════════════════
  // CONDIMENTS, SAUCES, SWEETENERS
  // ═══════════════════════════════════════════════════════════════════════════
  sal: { en: 'salt', de: 'Salz', pt: 'sal', zh: '盐' },
  pimienta: { en: 'pepper', de: 'Pfeffer', pt: 'pimenta', zh: '胡椒' },
  salsa: { en: 'sauce', de: 'Soße', pt: 'molho', zh: '酱料' },
  'salsa de tomate': {
    en: 'tomato sauce',
    de: 'Tomatensoße',
    pt: 'molho de tomate',
    zh: '番茄酱',
  },
  'salsa bbq': {
    en: 'BBQ sauce',
    de: 'BBQ-Soße',
    pt: 'molho BBQ',
    zh: 'BBQ酱',
  },
  'salsa ranch': {
    en: 'ranch dressing',
    de: 'Ranch-Dressing',
    pt: 'molho ranch',
    zh: '牧场酱',
  },
  mayonesa: {
    en: 'mayonnaise',
    de: 'Mayonnaise',
    pt: 'maionese',
    zh: '蛋黄酱',
  },
  mostaza: { en: 'mustard', de: 'Senf', pt: 'mostarda', zh: '芥末' },
  ketchup: { en: 'ketchup', de: 'Ketchup', pt: 'ketchup', zh: '番茄酱' },
  vinagreta: {
    en: 'vinaigrette',
    de: 'Vinaigrette',
    pt: 'vinagrete',
    zh: '油醋汁',
  },
  'aceite de oliva': {
    en: 'olive oil',
    de: 'Olivenöl',
    pt: 'azeite de oliva',
    zh: '橄榄油',
  },
  aceite: { en: 'oil', de: 'Öl', pt: 'óleo', zh: '油' },
  miel: { en: 'honey', de: 'Honig', pt: 'mel', zh: '蜂蜜' },
  'miel de abeja': {
    en: 'honey',
    de: 'Bienenhonig',
    pt: 'mel de abelha',
    zh: '蜂蜜',
  },
  azúcar: { en: 'sugar', de: 'Zucker', pt: 'açúcar', zh: '糖' },
  chocolate: {
    en: 'chocolate',
    de: 'Schokolade',
    pt: 'chocolate',
    zh: '巧克力',
  },
  canela: { en: 'cinnamon', de: 'Zimt', pt: 'canela', zh: '肉桂' },
  vainilla: { en: 'vanilla', de: 'Vanille', pt: 'baunilha', zh: '香草' },
  'dulce de leche': {
    en: 'caramel',
    de: 'Karamell',
    pt: 'doce de leite',
    zh: '焦糖',
  },
  mermelada: { en: 'jam', de: 'Marmelade', pt: 'geleia', zh: '果酱' },
  aderezo: { en: 'dressing', de: 'Dressing', pt: 'molho', zh: '调味酱' },
  pesto: { en: 'pesto', de: 'Pesto', pt: 'pesto', zh: '青酱' },

  // ═══════════════════════════════════════════════════════════════════════════
  // NUTS, SEEDS, TOPPINGS
  // ═══════════════════════════════════════════════════════════════════════════
  almendras: { en: 'almonds', de: 'Mandeln', pt: 'amêndoas', zh: '杏仁' },
  nueces: { en: 'walnuts', de: 'Walnüsse', pt: 'nozes', zh: '核桃' },
  maní: { en: 'peanuts', de: 'Erdnüsse', pt: 'amendoim', zh: '花生' },
  semillas: { en: 'seeds', de: 'Samen', pt: 'sementes', zh: '种子' },
  'semillas de chía': {
    en: 'chia seeds',
    de: 'Chiasamen',
    pt: 'sementes de chia',
    zh: '奇亚籽',
  },
  chía: { en: 'chia', de: 'Chia', pt: 'chia', zh: '奇亚' },
  linaza: { en: 'flaxseed', de: 'Leinsamen', pt: 'linhaça', zh: '亚麻籽' },
  crutones: { en: 'croutons', de: 'Croutons', pt: 'croutons', zh: '面包丁' },

  // ═══════════════════════════════════════════════════════════════════════════
  // DISHES & DISH TYPES
  // ═══════════════════════════════════════════════════════════════════════════
  pizza: { en: 'pizza', de: 'Pizza', pt: 'pizza', zh: '披萨' },
  hamburguesa: { en: 'burger', de: 'Burger', pt: 'hambúrguer', zh: '汉堡' },
  sándwich: { en: 'sandwich', de: 'Sandwich', pt: 'sanduíche', zh: '三明治' },
  sandwich: { en: 'sandwich', de: 'Sandwich', pt: 'sanduíche', zh: '三明治' },
  wrap: { en: 'wrap', de: 'Wrap', pt: 'wrap', zh: '卷饼' },
  tacos: { en: 'tacos', de: 'Tacos', pt: 'tacos', zh: '墨西哥卷饼' },
  burrito: { en: 'burrito', de: 'Burrito', pt: 'burrito', zh: '墨西哥卷' },
  quesadilla: {
    en: 'quesadilla',
    de: 'Quesadilla',
    pt: 'quesadilla',
    zh: '芝士玉米饼',
  },
  nachos: { en: 'nachos', de: 'Nachos', pt: 'nachos', zh: '玉米片' },
  ensalada: { en: 'salad', de: 'Salat', pt: 'salada', zh: '沙拉' },
  sopa: { en: 'soup', de: 'Suppe', pt: 'sopa', zh: '汤' },
  ceviche: { en: 'ceviche', de: 'Ceviche', pt: 'ceviche', zh: '酸橘汁腌鱼' },
  empanada: { en: 'empanada', de: 'Empanada', pt: 'empanada', zh: '馅饼' },
  empanadas: { en: 'empanadas', de: 'Empanadas', pt: 'empanadas', zh: '馅饼' },
  casado: {
    en: 'casado (traditional plate)',
    de: 'Casado (traditionelles Gericht)',
    pt: 'casado (prato tradicional)',
    zh: '卡萨多套餐',
  },
  chifrijo: { en: 'chifrijo', de: 'Chifrijo', pt: 'chifrijo', zh: '奇弗里霍' },
  patacones: {
    en: 'fried plantains',
    de: 'gebratene Kochbananen',
    pt: 'patacones',
    zh: '炸大蕉',
  },
  omelette: { en: 'omelette', de: 'Omelett', pt: 'omelete', zh: '煎蛋卷' },
  omelet: { en: 'omelette', de: 'Omelett', pt: 'omelete', zh: '煎蛋卷' },
  pancakes: { en: 'pancakes', de: 'Pfannkuchen', pt: 'panquecas', zh: '煎饼' },
  waffles: { en: 'waffles', de: 'Waffeln', pt: 'waffles', zh: '华夫饼' },
  bowl: { en: 'bowl', de: 'Bowl', pt: 'bowl', zh: '碗' },
  plato: { en: 'plate', de: 'Teller', pt: 'prato', zh: '盘' },
  'papas fritas': {
    en: 'french fries',
    de: 'Pommes Frites',
    pt: 'batatas fritas',
    zh: '薯条',
  },
  papas: { en: 'potatoes', de: 'Kartoffeln', pt: 'batatas', zh: '土豆' },
  plátano: {
    en: 'plantain',
    de: 'Kochbanane',
    pt: 'banana-da-terra',
    zh: '大蕉',
  },
  'plátano maduro': {
    en: 'sweet plantain',
    de: 'reife Kochbanane',
    pt: 'banana madura',
    zh: '甜大蕉',
  },
  helado: { en: 'ice cream', de: 'Eis', pt: 'sorvete', zh: '冰淇淋' },
  pastel: { en: 'cake', de: 'Kuchen', pt: 'bolo', zh: '蛋糕' },
  flan: { en: 'flan', de: 'Flan', pt: 'pudim', zh: '焦糖布丁' },
  brownie: { en: 'brownie', de: 'Brownie', pt: 'brownie', zh: '布朗尼' },
  churros: { en: 'churros', de: 'Churros', pt: 'churros', zh: '吉事果' },
  'tres leches': {
    en: 'tres leches cake',
    de: 'Tres-Leches-Kuchen',
    pt: 'bolo três leites',
    zh: '三奶蛋糕',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DRINKS / BEBIDAS
  // ═══════════════════════════════════════════════════════════════════════════
  café: { en: 'coffee', de: 'Kaffee', pt: 'café', zh: '咖啡' },
  'café americano': {
    en: 'americano',
    de: 'Americano',
    pt: 'café americano',
    zh: '美式咖啡',
  },
  espresso: { en: 'espresso', de: 'Espresso', pt: 'espresso', zh: '浓缩咖啡' },
  cappuccino: {
    en: 'cappuccino',
    de: 'Cappuccino',
    pt: 'cappuccino',
    zh: '卡布奇诺',
  },
  latte: { en: 'latte', de: 'Latte', pt: 'latte', zh: '拿铁' },
  mocha: { en: 'mocha', de: 'Mokka', pt: 'mocha', zh: '摩卡' },
  té: { en: 'tea', de: 'Tee', pt: 'chá', zh: '茶' },
  'té verde': {
    en: 'green tea',
    de: 'grüner Tee',
    pt: 'chá verde',
    zh: '绿茶',
  },
  'té negro': {
    en: 'black tea',
    de: 'schwarzer Tee',
    pt: 'chá preto',
    zh: '红茶',
  },
  jugo: { en: 'juice', de: 'Saft', pt: 'suco', zh: '果汁' },
  'jugo de naranja': {
    en: 'orange juice',
    de: 'Orangensaft',
    pt: 'suco de laranja',
    zh: '橙汁',
  },
  'jugo natural': {
    en: 'fresh juice',
    de: 'frischer Saft',
    pt: 'suco natural',
    zh: '鲜榨果汁',
  },
  agua: { en: 'water', de: 'Wasser', pt: 'água', zh: '水' },
  'agua mineral': {
    en: 'mineral water',
    de: 'Mineralwasser',
    pt: 'água mineral',
    zh: '矿泉水',
  },
  limonada: { en: 'lemonade', de: 'Limonade', pt: 'limonada', zh: '柠檬水' },
  batido: { en: 'smoothie', de: 'Smoothie', pt: 'vitamina', zh: '奶昔' },
  smoothie: { en: 'smoothie', de: 'Smoothie', pt: 'smoothie', zh: '冰沙' },
  milkshake: { en: 'milkshake', de: 'Milchshake', pt: 'milkshake', zh: '奶昔' },
  cerveza: { en: 'beer', de: 'Bier', pt: 'cerveja', zh: '啤酒' },
  vino: { en: 'wine', de: 'Wein', pt: 'vinho', zh: '葡萄酒' },
  'vino tinto': {
    en: 'red wine',
    de: 'Rotwein',
    pt: 'vinho tinto',
    zh: '红葡萄酒',
  },
  'vino blanco': {
    en: 'white wine',
    de: 'Weißwein',
    pt: 'vinho branco',
    zh: '白葡萄酒',
  },
  margarita: {
    en: 'margarita',
    de: 'Margarita',
    pt: 'margarita',
    zh: '玛格丽特',
  },
  mojito: { en: 'mojito', de: 'Mojito', pt: 'mojito', zh: '莫吉托' },
  sangría: { en: 'sangria', de: 'Sangria', pt: 'sangria', zh: '桑格利亚' },
  refresco: {
    en: 'soft drink',
    de: 'Erfrischungsgetränk',
    pt: 'refrigerante',
    zh: '软饮',
  },
  'refresco natural': {
    en: 'fresh drink',
    de: 'Naturgetränk',
    pt: 'refresco natural',
    zh: '鲜饮',
  },
  gaseosa: { en: 'soda', de: 'Limonade', pt: 'refrigerante', zh: '汽水' },

  // ═══════════════════════════════════════════════════════════════════════════
  // COOKING METHODS & DESCRIPTORS
  // ═══════════════════════════════════════════════════════════════════════════
  asado: { en: 'grilled', de: 'gegrillt', pt: 'assado', zh: '烤' },
  asada: { en: 'grilled', de: 'gegrillt', pt: 'assada', zh: '烤' },
  'a la parrilla': { en: 'grilled', de: 'gegrillt', pt: 'grelhado', zh: '烤' },
  'a la plancha': {
    en: 'griddled',
    de: 'auf der Platte',
    pt: 'na chapa',
    zh: '铁板',
  },
  frito: { en: 'fried', de: 'frittiert', pt: 'frito', zh: '炸' },
  frita: { en: 'fried', de: 'frittiert', pt: 'frita', zh: '炸' },
  fritos: { en: 'fried', de: 'frittiert', pt: 'fritos', zh: '炸' },
  'al horno': {
    en: 'baked',
    de: 'gebacken',
    pt: 'assado no forno',
    zh: '烘烤',
  },
  horneado: { en: 'baked', de: 'gebacken', pt: 'assado', zh: '烘烤' },
  salteado: { en: 'sautéed', de: 'angebraten', pt: 'salteado', zh: '炒' },
  salteada: { en: 'sautéed', de: 'angebraten', pt: 'salteada', zh: '炒' },
  salteadas: { en: 'sautéed', de: 'angebraten', pt: 'salteadas', zh: '炒' },
  hervido: { en: 'boiled', de: 'gekocht', pt: 'cozido', zh: '煮' },
  'al vapor': { en: 'steamed', de: 'gedämpft', pt: 'no vapor', zh: '蒸' },
  crudo: { en: 'raw', de: 'roh', pt: 'cru', zh: '生' },
  fresco: { en: 'fresh', de: 'frisch', pt: 'fresco', zh: '新鲜' },
  fresca: { en: 'fresh', de: 'frisch', pt: 'fresca', zh: '新鲜' },
  casero: { en: 'homemade', de: 'hausgemacht', pt: 'caseiro', zh: '自制' },
  casera: { en: 'homemade', de: 'hausgemacht', pt: 'caseira', zh: '自制' },
  artesanal: { en: 'artisan', de: 'handgemacht', pt: 'artesanal', zh: '手工' },
  natural: { en: 'natural', de: 'natürlich', pt: 'natural', zh: '天然' },
  orgánico: { en: 'organic', de: 'biologisch', pt: 'orgânico', zh: '有机' },
  tradicional: {
    en: 'traditional',
    de: 'traditionell',
    pt: 'tradicional',
    zh: '传统',
  },
  especial: { en: 'special', de: 'Spezial', pt: 'especial', zh: '特色' },
  clásica: { en: 'classic', de: 'klassisch', pt: 'clássica', zh: '经典' },
  clásico: { en: 'classic', de: 'klassisch', pt: 'clássico', zh: '经典' },
  grande: { en: 'large', de: 'groß', pt: 'grande', zh: '大份' },
  mediana: { en: 'medium', de: 'mittel', pt: 'média', zh: '中份' },
  mediano: { en: 'medium', de: 'mittel', pt: 'médio', zh: '中份' },
  pequeña: { en: 'small', de: 'klein', pt: 'pequena', zh: '小份' },
  pequeño: { en: 'small', de: 'klein', pt: 'pequeno', zh: '小份' },
  doble: { en: 'double', de: 'doppelt', pt: 'duplo', zh: '双份' },
  triple: { en: 'triple', de: 'dreifach', pt: 'triplo', zh: '三份' },
  mixto: { en: 'mixed', de: 'gemischt', pt: 'misto', zh: '混合' },
  mixta: { en: 'mixed', de: 'gemischt', pt: 'mista', zh: '混合' },
  relleno: { en: 'stuffed', de: 'gefüllt', pt: 'recheado', zh: '夹馅' },
  rellena: { en: 'stuffed', de: 'gefüllt', pt: 'recheada', zh: '夹馅' },
  ahumado: { en: 'smoked', de: 'geräuchert', pt: 'defumado', zh: '烟熏' },
  ahumada: { en: 'smoked', de: 'geräuchert', pt: 'defumada', zh: '烟熏' },
  picante: { en: 'spicy', de: 'scharf', pt: 'picante', zh: '辣' },
  dulce: { en: 'sweet', de: 'süß', pt: 'doce', zh: '甜' },
  salado: { en: 'savory', de: 'herzhaft', pt: 'salgado', zh: '咸' },
  caliente: { en: 'hot', de: 'heiß', pt: 'quente', zh: '热' },
  frío: { en: 'cold', de: 'kalt', pt: 'frio', zh: '冷' },
  fría: { en: 'cold', de: 'kalt', pt: 'fria', zh: '冷' },

  // ═══════════════════════════════════════════════════════════════════════════
  // CONNECTORS & PREPOSITIONS (for phrase translation)
  // ═══════════════════════════════════════════════════════════════════════════
  con: { en: 'with', de: 'mit', pt: 'com', zh: '配' },
  sin: { en: 'without', de: 'ohne', pt: 'sem', zh: '不加' },
  de: { en: 'of', de: 'von', pt: 'de', zh: '的' },
  del: { en: 'of the', de: 'des', pt: 'do', zh: '的' },
  y: { en: 'and', de: 'und', pt: 'e', zh: '和' },
  o: { en: 'or', de: 'oder', pt: 'ou', zh: '或' },
  al: { en: 'with', de: 'mit', pt: 'ao', zh: '配' },
  'a la': { en: 'in the style of', de: 'nach Art von', pt: 'à', zh: '风味' },
  en: { en: 'in', de: 'in', pt: 'em', zh: '在' },
  sobre: { en: 'on', de: 'auf', pt: 'sobre', zh: '上' },
  para: { en: 'for', de: 'für', pt: 'para', zh: '给' },
  los: { en: 'the', de: 'die', pt: 'os', zh: '' },
  las: { en: 'the', de: 'die', pt: 'as', zh: '' },
  el: { en: 'the', de: 'der', pt: 'o', zh: '' },
  la: { en: 'the', de: 'die', pt: 'a', zh: '' },
  un: { en: 'a', de: 'ein', pt: 'um', zh: '一' },
  una: { en: 'a', de: 'eine', pt: 'uma', zh: '一' },

  // ═══════════════════════════════════════════════════════════════════════════
  // OPTION GROUP NAMES
  // ═══════════════════════════════════════════════════════════════════════════
  tamaño: { en: 'Size', de: 'Größe', pt: 'Tamanho', zh: '大小' },
  proteína: { en: 'Protein', de: 'Protein', pt: 'Proteína', zh: '蛋白质' },
  extras: { en: 'Extras', de: 'Extras', pt: 'Extras', zh: '附加' },
  bebida: { en: 'Drink', de: 'Getränk', pt: 'Bebida', zh: '饮品' },
  acompañamiento: {
    en: 'Side',
    de: 'Beilage',
    pt: 'Acompanhamento',
    zh: '配菜',
  },
  'tipo de leche': {
    en: 'Milk type',
    de: 'Milchart',
    pt: 'Tipo de leite',
    zh: '牛奶类型',
  },
  'tipo de pan': {
    en: 'Bread type',
    de: 'Brotart',
    pt: 'Tipo de pão',
    zh: '面包类型',
  },
  'punto de cocción': {
    en: 'Doneness',
    de: 'Garstufe',
    pt: 'Ponto de cozimento',
    zh: '熟度',
  },
  extra: { en: 'Extra', de: 'Extra', pt: 'Extra', zh: '额外' },
  'sin gluten': {
    en: 'Gluten-free',
    de: 'Glutenfrei',
    pt: 'Sem glúten',
    zh: '无麸质',
  },
  'con queso': {
    en: 'With cheese',
    de: 'Mit Käse',
    pt: 'Com queijo',
    zh: '加奶酪',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PIZZA-SPECIFIC (Emilius Pizza)
  // ═══════════════════════════════════════════════════════════════════════════
  margherita: {
    en: 'Margherita',
    de: 'Margherita',
    pt: 'Margherita',
    zh: '玛格丽特',
  },
  hawaiana: { en: 'Hawaiian', de: 'Hawaii', pt: 'Havaiana', zh: '夏威夷' },
  vegetariana: {
    en: 'Vegetarian',
    de: 'Vegetarisch',
    pt: 'Vegetariana',
    zh: '素食',
  },
  'cuatro quesos': {
    en: 'Four Cheese',
    de: 'Vier Käse',
    pt: 'Quatro Queijos',
    zh: '四种奶酪',
  },
  bbq: { en: 'BBQ', de: 'BBQ', pt: 'BBQ', zh: 'BBQ' },
  suprema: { en: 'Supreme', de: 'Supreme', pt: 'Suprema', zh: '至尊' },
  diavola: { en: 'Diavola', de: 'Diavola', pt: 'Diavola', zh: '辣味' },
  carbonara: {
    en: 'Carbonara',
    de: 'Carbonara',
    pt: 'Carbonara',
    zh: '培根蛋',
  },
  napolitana: {
    en: 'Neapolitan',
    de: 'Neapolitanisch',
    pt: 'Napolitana',
    zh: '那不勒斯',
  },
  masa: { en: 'dough', de: 'Teig', pt: 'massa', zh: '面团' },
  'masa delgada': {
    en: 'thin crust',
    de: 'dünner Teig',
    pt: 'massa fina',
    zh: '薄底',
  },
  'masa gruesa': {
    en: 'thick crust',
    de: 'dicker Teig',
    pt: 'massa grossa',
    zh: '厚底',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // KABU-SPECIFIC DISH NAMES
  // ═══════════════════════════════════════════════════════════════════════════
  'bowl néctar de montaña': {
    en: 'Mountain Nectar Bowl',
    de: 'Berg-Nektar-Bowl',
    pt: 'Bowl Néctar da Montanha',
    zh: '山间甘露碗',
  },
  'omelette monte y huerto': {
    en: 'Mountain & Garden Omelette',
    de: 'Berg- und Garten-Omelett',
    pt: 'Omelete Monte e Horta',
    zh: '山野花园煎蛋卷',
  },
  néctar: { en: 'nectar', de: 'Nektar', pt: 'néctar', zh: '甘露' },
  montaña: { en: 'mountain', de: 'Berg', pt: 'montanha', zh: '山' },
  monte: { en: 'mountain', de: 'Berg', pt: 'monte', zh: '山' },
  huerto: { en: 'garden', de: 'Garten', pt: 'horta', zh: '花园' },
  valle: { en: 'valley', de: 'Tal', pt: 'vale', zh: '山谷' },
  campo: { en: 'countryside', de: 'Land', pt: 'campo', zh: '田野' },
  tierra: { en: 'earth', de: 'Erde', pt: 'terra', zh: '大地' },
  mar: { en: 'sea', de: 'Meer', pt: 'mar', zh: '海' },
  río: { en: 'river', de: 'Fluss', pt: 'rio', zh: '河' },
  bosque: { en: 'forest', de: 'Wald', pt: 'floresta', zh: '森林' },
  jardín: { en: 'garden', de: 'Garten', pt: 'jardim', zh: '花园' },
  sol: { en: 'sun', de: 'Sonne', pt: 'sol', zh: '阳光' },
  fuego: { en: 'fire', de: 'Feuer', pt: 'fogo', zh: '火' },
  corazón: { en: 'heart', de: 'Herz', pt: 'coração', zh: '心' },
  sabor: { en: 'flavor', de: 'Geschmack', pt: 'sabor', zh: '风味' },
  sabores: { en: 'flavors', de: 'Geschmäcker', pt: 'sabores', zh: '风味' },
  receta: { en: 'recipe', de: 'Rezept', pt: 'receita', zh: '食谱' },
  chef: { en: 'chef', de: 'Koch', pt: 'chef', zh: '厨师' },
  'del chef': { en: "chef's", de: 'des Kochs', pt: 'do chef', zh: '厨师的' },
  'de la casa': { en: 'house', de: 'Haus-', pt: 'da casa', zh: '招牌' },
  'del día': { en: 'of the day', de: 'des Tages', pt: 'do dia', zh: '每日' },
  acompañado: {
    en: 'served with',
    de: 'serviert mit',
    pt: 'acompanhado',
    zh: '搭配',
  },
  acompañada: {
    en: 'served with',
    de: 'serviert mit',
    pt: 'acompanhada',
    zh: '搭配',
  },
  servido: { en: 'served', de: 'serviert', pt: 'servido', zh: '配上' },
  servida: { en: 'served', de: 'serviert', pt: 'servida', zh: '配上' },
  porción: { en: 'portion', de: 'Portion', pt: 'porção', zh: '份' },
  combo: { en: 'combo', de: 'Kombi', pt: 'combo', zh: '套餐' },
}

// ─── Translation cache ──────────────────────────────────────────────────────
const cache = new Map<string, string>()

function getCacheKey(text: string, lang: Language): string {
  return `${lang}::${text}`
}

/**
 * Try to find a multi-word phrase match starting from position `start` in the words array.
 * Returns [translatedPhrase, wordsConsumed] or null if no phrase match found.
 */
function findPhraseMatch(
  words: string[],
  start: number,
  lang: LangKey,
): [string, number] | null {
  // Try longest phrases first (up to 5 words)
  const maxLen = Math.min(5, words.length - start)
  for (let len = maxLen; len >= 2; len--) {
    const phrase = words.slice(start, start + len).join(' ')
    const entry = dictionary[phrase]
    if (entry && entry[lang]) {
      return [entry[lang]!, len]
    }
  }
  return null
}

/**
 * Translate a single segment (no commas) word-by-word with phrase matching.
 */
function translateSegment(segment: string, lang: LangKey): string {
  const trimmed = segment.trim()
  if (!trimmed) return trimmed

  // Try full segment match first
  const fullEntry = dictionary[trimmed.toLowerCase()]
  if (fullEntry && fullEntry[lang]) {
    // Preserve original casing
    const translated = fullEntry[lang]!
    if (trimmed[0] === trimmed[0].toUpperCase()) {
      return translated.charAt(0).toUpperCase() + translated.slice(1)
    }
    return translated
  }

  // Word-by-word with phrase matching
  const words = trimmed.split(/\s+/)
  const result: string[] = []
  let i = 0

  while (i < words.length) {
    const word = words[i]
    const wordLower = word.toLowerCase()

    // Try multi-word phrase match first
    const phraseMatch = findPhraseMatch(
      words.map((w) => w.toLowerCase()),
      i,
      lang,
    )
    if (phraseMatch) {
      const [translated, consumed] = phraseMatch
      // Preserve casing of first word
      if (word[0] === word[0].toUpperCase()) {
        result.push(translated.charAt(0).toUpperCase() + translated.slice(1))
      } else {
        result.push(translated)
      }
      i += consumed
      continue
    }

    // Single word match
    const entry = dictionary[wordLower]
    if (entry && entry[lang]) {
      const translated = entry[lang]!
      // Preserve casing
      if (word[0] === word[0].toUpperCase()) {
        result.push(translated.charAt(0).toUpperCase() + translated.slice(1))
      } else {
        result.push(translated)
      }
    } else {
      // Keep original word
      result.push(word)
    }
    i++
  }

  return result.join(' ')
}

/**
 * Translate a Spanish string to the target language using the static dictionary.
 * - Returns original text immediately for 'es'
 * - Handles comma-separated ingredient lists
 * - Falls back to original Spanish if no translation found
 * - Never throws
 */
export function translateText(text: string, lang: Language): string {
  try {
    if (!text || lang === 'es') return text

    const cacheKey = getCacheKey(text, lang)
    const cached = cache.get(cacheKey)
    if (cached) return cached

    const langKey = lang as LangKey

    // Try full-text exact match first (for dish names, categories)
    const fullMatch = dictionary[text.toLowerCase().trim()]
    if (fullMatch && fullMatch[langKey]) {
      const translated = fullMatch[langKey]!
      const result =
        text[0] === text[0].toUpperCase()
          ? translated.charAt(0).toUpperCase() + translated.slice(1)
          : translated
      cache.set(cacheKey, result)
      return result
    }

    // Split by commas and translate each segment
    if (text.includes(',')) {
      const segments = text.split(',')
      const translatedSegments = segments.map((seg) =>
        translateSegment(seg, langKey),
      )
      const result = translatedSegments.join(',')
      cache.set(cacheKey, result)
      return result
    }

    // Single segment translation
    const result = translateSegment(text, langKey)
    cache.set(cacheKey, result)
    return result
  } catch {
    return text || ''
  }
}

/**
 * Translate a menu item's display fields (name + description).
 * Returns a new object — never mutates the original.
 */
export function translateMenuItem(
  item: { name: string; description?: string | null },
  lang: Language,
): { name: string; description: string | null } {
  try {
    if (lang === 'es') {
      return { name: item.name, description: item.description ?? null }
    }
    return {
      name: translateText(item.name, lang),
      description: item.description
        ? translateText(item.description, lang)
        : null,
    }
  } catch {
    return { name: item.name, description: item.description ?? null }
  }
}

/**
 * Translate a category name.
 */
export function translateCategory(
  categoryName: string,
  lang: Language,
): string {
  return translateText(categoryName, lang)
}
